import { HttpContextToken, HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { NotificationService } from './notification';

export const ERROR_HANDLED = new HttpContextToken<boolean>(() => false);

const DEFAULT_ERROR_MESSAGE = 'Something went wrong connecting with the server. Try again later.';

const ERROR_MESSAGES_BY_CODE: Readonly<Record<string, string>> = {
  SUBSCRIPTION_ALREADY_IN_PROGRESS:
    'We are already processing this subscription. Please do not submit it again.',
  ORDER_ALREADY_IN_PROGRESS: 'We are already processing this order. Please do not submit it again.',
  SECRET_GARDEN_RESERVATION_ALREADY_IN_PROGRESS:
    'We are already processing this reservation. Please do not submit it again.',
  PAYMENT_COMPENSATION_FAILED:
    'We could not confirm the final status of your payment. Please reach out to us before trying again.',
};

interface ErrorBody {
  code?: string;
  message?: string;
}

const isErrorBody = (value: unknown): value is ErrorBody => {
  if (typeof value !== 'object' || value === null) return false;

  const code = 'code' in value ? value.code : undefined;
  const message = 'message' in value ? value.message : undefined;

  return (
    (code === undefined || typeof code === 'string') &&
    (message === undefined || typeof message === 'string')
  );
};

const resolveErrorMessage = (error: HttpErrorResponse): string => {
  const body: unknown = error.error;

  if (!isErrorBody(body)) return DEFAULT_ERROR_MESSAGE;

  const knownMessage = body.code ? ERROR_MESSAGES_BY_CODE[body.code] : undefined;

  return knownMessage || body.message || DEFAULT_ERROR_MESSAGE;
};

export const errorHandlerInterceptor: HttpInterceptorFn = (req, next) => {
  const notification = inject(NotificationService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (req.context.has(ERROR_HANDLED) === true) {
        return throwError(() => error);
      }

      notification.push({ type: 'error', message: resolveErrorMessage(error) });

      return throwError(() => error);
    }),
  );
};
